'use client'
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react'
import { Heading, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { TabWithImageProps, TabWithImageDataItem } from './types.js'
import { motion, AnimatePresence } from 'framer-motion'

const componentType = 'image'
const componentName = 'tab_with_image'

const TabWithImage = React.forwardRef<HTMLDivElement, TabWithImageProps>(({ data, className }, ref) => {
  const { title, subtitle, desc, image, mobileImage, datalist = [] } = data

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: desc,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  const handleClick = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    setActiveIndex(index)
    ;(e.target as HTMLElement).scrollIntoView({
      behavior: 'smooth', // 平滑滚动
      inline: 'center', // 横向居中对齐
      block: 'nearest', // 垂直方向不动
    })
  }

  const [activeIndex, setActiveIndex] = useState(0)
  const tabRefs = useRef<Array<HTMLDivElement | null>>([]) // 记录每个tab的位置
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 })

  useEffect(() => {
    const current = tabRefs.current[activeIndex]
    if (current) {
      const { offsetLeft, offsetWidth } = current
      setSliderStyle({ left: offsetLeft, width: offsetWidth })
    }
  }, [activeIndex, datalist.length])

  return (
    <section
      ref={boxRef}
      data-ui-component-id="TabWithImage"
      className={cn(
        'tab-with-image l:gap-[24px] min-md:justify-between min-l:gap-[20px] flex text-white xl:flex-col',
        className
      )}
    >
      <div className="tab-with-image__content min-md:gap-[24px] laptop:basis-[36%] inline-flex flex-col justify-center">
        <div className="tab-with-image__header">
          <Heading as={'h3'} size={4} html={title} className="tab-with-image__title" />
          <Text
            as={'p'}
            size={1}
            html={desc}
            className="tab-with-image__description tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-[14px]"
          />
        </div>

        <div className="tab-with-image__tabs-wrapper md:scrollbar-hidden relative md:overflow-hidden md:overflow-x-scroll">
          <div className="tab-with-image__tabs rounded-btn relative inline-flex bg-[#1D1D1F] px-[4px] md:my-[24px]">
            {/* 滑动背景 */}
            <div
              className="tab-with-image__slider rounded-btn absolute inset-y-0 bg-white transition-all duration-300 ease-in-out"
              style={{
                left: sliderStyle.left,
                width: sliderStyle.width,
              }}
            />

            {/* Tab Items */}
            {datalist.map((item: TabWithImageDataItem, index: number) => (
              <div
                key={index}
                ref={el => {
                  tabRefs.current[index] = el
                }}
                onClick={e => handleClick(index, e)}
                className={cn(
                  'tab-with-image__tab rounded-btn min-xxl:px-[28px] min-xxl:py-[15px] relative z-10 cursor-pointer px-[20px] py-[10px] text-center text-[12px] font-medium transition-colors duration-300 md:px-[20px] md:py-[10px]',
                  activeIndex === index ? 'tab-with-image__tab--active text-black' : 'text-white'
                )}
              >
                <Heading
                  as="h1"
                  size={1}
                  html={item?.title}
                  className="tab-with-image__tab-title text-balance-normal !whitespace-nowrap md:text-[14px]"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="tab-with-image__image-wrapper min-xxl:aspect-[824/560] min-xxl:max-w-[824px] tablet:aspect-[704/360] laptop:aspect-[744/336] laptop:basis-[64%] desktop:aspect-[648/448] relative w-full shrink md:aspect-[358/360]">
        <AnimatePresence mode="wait">
          {datalist[activeIndex] && (
            <motion.div
              key={datalist[activeIndex].image?.url}
              {...({
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 },
                transition: { duration: 0.3 },
              } as any)}
              className="tab-with-image__image-motion absolute left-0 top-0 w-full"
            >
              <Picture
                source={`${datalist[activeIndex].image?.url} ,${datalist[activeIndex].imgPad?.url || datalist[activeIndex].image?.url} 1440, ${datalist[activeIndex].imageMob?.url || datalist[activeIndex].image?.url} 767`}
                alt={datalist[activeIndex].image?.alt}
                className="tab-with-image__image rounded-box min-xxl:aspect-[824/560] min-xxl:max-w-[824px] tablet:aspect-[704/360] laptop:aspect-[744/336] desktop:aspect-[648/448] md:aspect-[358/360] "
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
})

TabWithImage.displayName = 'TabWithImage'

export default withLayout(TabWithImage)
